import { useId, useState } from 'react'
import { ChevronDown, ChevronRight, Sparkles } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { FileIcon, miniappIconProps } from '@papan/core-ui/ui'
import type { RecentWorkGroup } from '@papan/files/client'
import { formatRelativeDate } from '../formatters'
import { groupTitle, isSingleFile, locationText, reasonLine, recentTimeLabel, type RecentWords } from './recentText'

export interface RecentNav {
  /** Reveal a file in its folder (the Drive's `?reveal=`). */
  revealFile: (file: { id: string; parentId: string | null }) => void
  /** Open the group's place: the app, the project, the folder (or the top of the drive). */
  openPlace: (group: RecentWorkGroup) => void
}

/**
 * #653 — one piece of recent work, in Drive Home's list style (`.ws-home-list` rows).
 *  - An app: one row, never its files; the row opens the app.
 *  - A single file: reads as that file; the row reveals it in its folder.
 *  - Several files: the row expands to its files (up to 50, then "+N more" opens the folder);
 *    **Open** opens the project or folder.
 */
export function RecentGroupRow({ group, words, nav }: { group: RecentWorkGroup; words: RecentWords; nav: RecentNav }) {
  const { t } = useTranslation('files')
  const [expanded, setExpanded] = useState(false)
  const filesId = useId()
  const single = isSingleFile(group)
  const isApp = group.kind === 'app'
  const expandable = !isApp && !single
  const title = groupTitle(group, words)
  const meta = [reasonLine(group, words), locationText(group, words)].join(' · ')
  const time = recentTimeLabel(group.latestAt, new Date(), formatRelativeDate, words.t)

  const icon = isApp && group.place
    ? <FileIcon name={group.place.name} isFolder size={16} {...miniappIconProps({ id: group.place.id, name: group.place.name, metadataJson: group.place.metadataJson ?? null })} />
    : single
      ? <FileIcon name={group.files[0].name} size={16} />
      : <FileIcon name={group.place?.name ?? ''} isFolder size={16} />

  const onMain = () => {
    if (isApp) nav.openPlace(group)
    else if (single) nav.revealFile(group.files[0])
    else setExpanded((v) => !v)
  }
  const more = group.fileCount - group.files.length

  return (
    <div className="ws-recent-row" data-testid="recent-row" data-kind={group.kind}>
      <div className="flex items-center gap-1.5 pr-2 hover:themed-bg-tertiary transition-colors">
        <button
          type="button"
          onClick={onMain}
          className="flex-1 min-w-0 flex items-center gap-3 pl-3.5 py-2.5 text-left"
          {...(expandable ? { 'aria-expanded': expanded, 'aria-controls': filesId } : {})}
          data-testid="recent-row-main"
        >
          {icon}
          <span className="flex-1 min-w-0">
            <span className="block text-sm themed-text truncate" title={title}>{title}</span>
            <span className="flex items-center gap-1 text-xs themed-text-muted min-w-0">
              {group.withAI && <Sparkles size={11} className="shrink-0 text-indigo-400" aria-hidden />}
              {/* A phone has no room for a time column: the time leads this line instead. */}
              <span className="truncate" title={meta}><span className="sm:hidden" data-testid="recent-row-time-inline">{time} · </span>{meta}</span>
            </span>
          </span>
          {expandable && (
            <span className="shrink-0 themed-text-muted" aria-hidden>
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          )}
          <span className="hidden sm:block text-xs themed-text-muted w-32 text-right shrink-0">{time}</span>
        </button>
        {/* Open, or the same space empty, so every row's time lines up (on a phone no time column: no empty space). */}
        <span className={`shrink-0 sm:w-14 justify-end ${single ? 'hidden sm:flex' : 'flex'}`}>
          {!single && (
            <button
              type="button"
              onClick={() => nav.openPlace(group)}
              className="text-xs px-2 py-1 rounded-md themed-text-muted hover:themed-text hover:themed-bg-secondary transition-colors"
              data-testid="recent-row-open"
            >
              {t('driveHome.recent.open')}
            </button>
          )}
        </span>
      </div>
      {expandable && expanded && (
        <ul id={filesId} className="pb-1.5" aria-label={title} data-testid="recent-row-files">
          {group.files.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => nav.revealFile(f)}
                className="w-full flex items-center gap-2.5 pl-[46px] pr-4 py-1.5 text-left hover:themed-bg-tertiary transition-colors"
                data-testid="recent-row-file"
              >
                <FileIcon name={f.name} size={14} />
                <span className="flex-1 min-w-0 text-sm themed-text truncate">{f.name}</span>
                {f.change === 'ai' && <Sparkles size={11} className="shrink-0 text-indigo-400" aria-hidden />}
                <span className="text-xs themed-text-muted sm:w-32 text-right shrink-0 sm:mr-14">{recentTimeLabel(f.at, new Date(), formatRelativeDate, words.t)}</span>
              </button>
            </li>
          ))}
          {more > 0 && (
            <li>
              <button
                type="button"
                onClick={() => nav.openPlace(group)}
                className="pl-[46px] pr-4 py-1.5 text-xs text-indigo-400 hover:text-indigo-300"
                data-testid="recent-row-more"
              >
                {t('driveHome.recent.moreFiles', { count: more })}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

/** Rows in one `.ws-home-list` frame. */
export function RecentGroupList({ groups, words, nav }: { groups: readonly RecentWorkGroup[]; words: RecentWords; nav: RecentNav }) {
  return (
    <div className="ws-home-list">
      {groups.map((g) => <RecentGroupRow key={g.id} group={g} words={words} nav={nav} />)}
    </div>
  )
}
